import { EquipmentSlot, ItemRarity } from '@shared/types';

// Base path for icon assets
const ICONS_7SOUL = 'sprites/items/icons_7soul';
const ICONS_DB32 = 'sprites/items/db32_items';

/**
 * Custom icon folder — drop PNGs here named {baseId}.png
 * and list them in manifest.json for auto-loading.
 */
export const CUSTOM_ICONS_PATH = 'sprites/items/icons';

/** Set of baseIds that have custom icons successfully loaded */
const loadedCustomIcons = new Set<string>();

/** Register a baseId as having a custom icon loaded */
export function registerCustomIcon(baseId: string): void {
  loadedCustomIcons.add(baseId);
}

/** Check if a baseId has a custom icon */
export function hasCustomIcon(baseId: string): boolean {
  return loadedCustomIcons.has(baseId);
}

// Icon variants per baseId, indexed by rarity quality tier
// Each entry is an array: [normal, magic, rare, sacred/legendary]
interface IconVariants {
  normal: string;
  magic: string;
  rare: string;
  sacred: string;
}

function s7(file: string): string {
  return `${ICONS_7SOUL}/${file}`;
}

function db32(file: string): string {
  return `${ICONS_DB32}/${file}`;
}

// baseId → icon variants by rarity
export const ITEM_ICON_MAP: Record<string, IconVariants> = {
  // === Weapons ===
  kilic:         { normal: s7('W_Sword001.png'), magic: s7('W_Sword004.png'), rare: s7('W_Sword009.png'), sacred: s7('W_Gold_Sword.png') },
  uzun_kilic:    { normal: s7('W_Sword005.png'), magic: s7('W_Sword007.png'), rare: s7('W_Sword012.png'), sacred: s7('W_Gold_Sword.png') },
  balta:         { normal: s7('W_Axe001.png'),   magic: s7('W_Axe003.png'),   rare: s7('W_Axe007.png'),   sacred: s7('W_Gold_Axe.png') },
  savas_baltasi: { normal: s7('W_Axe005.png'),   magic: s7('W_Axe008.png'),   rare: s7('W_Axe011.png'),   sacred: s7('W_Gold_Axe.png') },
  yay:           { normal: s7('W_Bow01.png'),    magic: s7('W_Bow04.png'),    rare: s7('W_Bow08.png'),    sacred: s7('W_Gold_Bow.png') },
  kompozit_yay:  { normal: s7('W_Bow06.png'),    magic: s7('W_Bow09.png'),    rare: s7('W_Bow13.png'),    sacred: s7('W_Gold_Bow.png') },
  asa:           { normal: s7('W_Staff01.png'),  magic: s7('W_Staff03.png'),  rare: s7('W_Staff05.png'),  sacred: s7('W_Staff07.png') },
  kam_asasi:     { normal: s7('W_Staff04.png'),  magic: s7('W_Staff05.png'),  rare: s7('W_Staff07.png'),  sacred: s7('W_Staff08.png') },

  // === Chest Armor ===
  deri_zirh:      { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour03.png') },
  demir_zirh:     { normal: s7('A_Armour01.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
  kumasli_kaftan: { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour03.png') },
  celik_zirh:     { normal: s7('A_Armour03.png'),   magic: s7('A_Armour03.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
  sert_deri:      { normal: s7('A_Armor04.png'),    magic: s7('A_Armour01.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },

  // === Helmets ===
  demir_tolgasi: { normal: s7('C_Elm01.png'),  magic: s7('C_Elm03.png'),  rare: s7('C_Elm04.png'),  sacred: s7('C_Elm04.png') },
  'börk':        { normal: s7('C_Hat01.png'),  magic: s7('C_Hat02.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Hat02.png') },
  celik_tolga:   { normal: s7('C_Elm03.png'),  magic: s7('C_Elm04.png'),  rare: s7('C_Elm04.png'),  sacred: s7('C_Elm04.png') },
  'avcı_sapkasi':{ normal: s7('C_Hat02.png'),  magic: s7('C_Hat02.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Hat02.png') },

  // === Boots ===
  cizme:          { normal: s7('A_Shoes01.png'), magic: s7('A_Shoes02.png'), rare: s7('A_Shoes03.png'), sacred: s7('A_Shoes04.png') },
  savas_cizmesi:  { normal: s7('A_Shoes03.png'), magic: s7('A_Shoes04.png'), rare: s7('A_Shoes05.png'), sacred: s7('A_Shoes06.png') },
  hafif_cizme:    { normal: s7('A_Shoes02.png'), magic: s7('A_Shoes03.png'), rare: s7('A_Shoes04.png'), sacred: s7('A_Shoes05.png') },

  // === Gloves ===
  eldiven:        { normal: s7('W_Fist001.png'), magic: s7('W_Fist002.png'), rare: s7('W_Fist003.png'), sacred: s7('W_Fist004.png') },
  savas_eldiveni: { normal: s7('W_Fist003.png'), magic: s7('W_Fist004.png'), rare: s7('W_Fist005.png'), sacred: s7('W_Fist005.png') },
  deri_eldiven:   { normal: s7('W_Fist002.png'), magic: s7('W_Fist003.png'), rare: s7('W_Fist004.png'), sacred: s7('W_Fist005.png') },

  // === Belt ===
  kemer: { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },

  // === Accessories ===
  muska:  { normal: s7('Ac_Necklace01.png'), magic: s7('Ac_Necklace03.png'), rare: s7('Ac_Necklace05.png'), sacred: s7('Ac_Necklace07.png') },
  yuzuk:  { normal: s7('Ac_Ring01.png'),     magic: s7('Ac_Ring01.png'),     rare: s7('Ac_Ring02.png'),     sacred: s7('Ac_Ring02.png') },

  // === Capes ===
  yun_pelerin:    { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour02.png') },
  ipek_pelerin:   { normal: s7('A_Clothing02.png'), magic: s7('A_Armour01.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },
  savas_pelerini: { normal: s7('A_Armour02.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
};

// Slot-based fallback icons (when baseId is unknown)
export const SLOT_FALLBACK_ICONS: Record<EquipmentSlot, string> = {
  weapon:  s7('W_Sword001.png'),
  chest:   s7('A_Armour01.png'),
  head:    s7('C_Elm01.png'),
  boots:   s7('A_Shoes01.png'),
  gloves:  s7('W_Fist001.png'),
  belt:    s7('A_Armor05.png'),
  cape:    s7('A_Clothing01.png'),
  amulet:  s7('Ac_Necklace01.png'),
  ring1:   s7('Ac_Ring01.png'),
  ring2:   s7('Ac_Ring02.png'),
  offhand: db32('shield_metal.png'),
};

// Consumable / currency / material icons
export const MISC_ITEM_ICONS: Record<string, string> = {
  heal_potion:  s7('P_Red01.png'),
  mana_potion:  s7('P_Blue01.png'),
  gold:         s7('I_GoldCoin.png'),
  gem:          s7('I_Ruby.png'),
  crystal:      s7('I_Crystal01.png'),
};

// Registry base ID → icon variants mapping (pattern-based)
// Maps new BaseItemRegistry IDs (head_ar_1, weapon_sword_30, etc.) to icons
const REGISTRY_ICON_MAP: Record<string, IconVariants> = {
  // === Head ===
  head_ar_1:  { normal: s7('C_Elm01.png'),  magic: s7('C_Elm01.png'),  rare: s7('C_Elm03.png'),  sacred: s7('C_Elm04.png') },
  head_ar_15: { normal: s7('C_Elm03.png'),  magic: s7('C_Elm03.png'),  rare: s7('C_Elm04.png'),  sacred: s7('C_Elm04.png') },
  head_ar_30: { normal: s7('C_Elm04.png'),  magic: s7('C_Elm04.png'),  rare: s7('C_Elm04.png'),  sacred: s7('C_Elm04.png') },
  head_ev_1:  { normal: s7('C_Hat01.png'),  magic: s7('C_Hat01.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Hat02.png') },
  head_ev_15: { normal: s7('C_Hat02.png'),  magic: s7('C_Hat02.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Hat02.png') },
  head_ev_30: { normal: s7('C_Hat02.png'),  magic: s7('C_Hat02.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Hat02.png') },
  head_es_1:  { normal: s7('C_Hat01.png'),  magic: s7('C_Hat01.png'),  rare: s7('C_Hat02.png'),  sacred: s7('C_Elm04.png') },
  head_es_15: { normal: s7('C_Hat02.png'),  magic: s7('C_Hat02.png'),  rare: s7('C_Elm03.png'),  sacred: s7('C_Elm04.png') },
  head_es_30: { normal: s7('C_Elm03.png'),  magic: s7('C_Elm04.png'),  rare: s7('C_Elm04.png'),  sacred: s7('C_Elm04.png') },

  // === Chest ===
  chest_ar_1:  { normal: s7('A_Armour01.png'),   magic: s7('A_Armour01.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },
  chest_ar_15: { normal: s7('A_Armour02.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
  chest_ar_30: { normal: s7('A_Armour03.png'),   magic: s7('A_Armour03.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
  chest_ev_1:  { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing01.png'), rare: s7('A_Clothing02.png'), sacred: s7('A_Armour01.png') },
  chest_ev_15: { normal: s7('A_Clothing02.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour02.png') },
  chest_ev_30: { normal: s7('A_Armour01.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },
  chest_es_1:  { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing01.png'), rare: s7('A_Clothing02.png'), sacred: s7('A_Armour01.png') },
  chest_es_15: { normal: s7('A_Clothing02.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour02.png') },
  chest_es_30: { normal: s7('A_Armour01.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },

  // === Gloves ===
  gloves_ar_1:  { normal: s7('W_Fist001.png'), magic: s7('W_Fist002.png'), rare: s7('W_Fist003.png'), sacred: s7('W_Fist004.png') },
  gloves_ar_15: { normal: s7('W_Fist002.png'), magic: s7('W_Fist003.png'), rare: s7('W_Fist004.png'), sacred: s7('W_Fist005.png') },
  gloves_ar_30: { normal: s7('W_Fist003.png'), magic: s7('W_Fist004.png'), rare: s7('W_Fist005.png'), sacred: s7('W_Fist005.png') },
  gloves_ev_1:  { normal: s7('W_Fist001.png'), magic: s7('W_Fist002.png'), rare: s7('W_Fist003.png'), sacred: s7('W_Fist004.png') },
  gloves_ev_15: { normal: s7('W_Fist002.png'), magic: s7('W_Fist003.png'), rare: s7('W_Fist004.png'), sacred: s7('W_Fist005.png') },
  gloves_ev_30: { normal: s7('W_Fist003.png'), magic: s7('W_Fist004.png'), rare: s7('W_Fist005.png'), sacred: s7('W_Fist005.png') },
  gloves_es_1:  { normal: s7('W_Fist001.png'), magic: s7('W_Fist002.png'), rare: s7('W_Fist003.png'), sacred: s7('W_Fist004.png') },
  gloves_es_15: { normal: s7('W_Fist002.png'), magic: s7('W_Fist003.png'), rare: s7('W_Fist004.png'), sacred: s7('W_Fist005.png') },
  gloves_es_30: { normal: s7('W_Fist003.png'), magic: s7('W_Fist004.png'), rare: s7('W_Fist005.png'), sacred: s7('W_Fist005.png') },

  // === Boots ===
  boots_ar_1:  { normal: s7('A_Shoes01.png'), magic: s7('A_Shoes02.png'), rare: s7('A_Shoes03.png'), sacred: s7('A_Shoes04.png') },
  boots_ar_15: { normal: s7('A_Shoes02.png'), magic: s7('A_Shoes03.png'), rare: s7('A_Shoes04.png'), sacred: s7('A_Shoes05.png') },
  boots_ar_30: { normal: s7('A_Shoes03.png'), magic: s7('A_Shoes04.png'), rare: s7('A_Shoes05.png'), sacred: s7('A_Shoes06.png') },
  boots_ev_1:  { normal: s7('A_Shoes01.png'), magic: s7('A_Shoes02.png'), rare: s7('A_Shoes03.png'), sacred: s7('A_Shoes04.png') },
  boots_ev_15: { normal: s7('A_Shoes02.png'), magic: s7('A_Shoes03.png'), rare: s7('A_Shoes04.png'), sacred: s7('A_Shoes05.png') },
  boots_ev_30: { normal: s7('A_Shoes03.png'), magic: s7('A_Shoes04.png'), rare: s7('A_Shoes05.png'), sacred: s7('A_Shoes06.png') },
  boots_es_1:  { normal: s7('A_Shoes01.png'), magic: s7('A_Shoes02.png'), rare: s7('A_Shoes03.png'), sacred: s7('A_Shoes04.png') },
  boots_es_15: { normal: s7('A_Shoes02.png'), magic: s7('A_Shoes03.png'), rare: s7('A_Shoes04.png'), sacred: s7('A_Shoes05.png') },
  boots_es_30: { normal: s7('A_Shoes03.png'), magic: s7('A_Shoes04.png'), rare: s7('A_Shoes05.png'), sacred: s7('A_Shoes06.png') },

  // === Belts ===
  belt_ar_1:  { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },
  belt_ar_15: { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },
  belt_ar_30: { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },
  belt_ev_1:  { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },
  belt_ev_15: { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },
  belt_ev_30: { normal: s7('A_Armor05.png'), magic: s7('A_Armor05.png'), rare: s7('A_Armor05.png'), sacred: s7('A_Armor05.png') },

  // === Weapons ===
  weapon_sword_1:   { normal: s7('W_Sword001.png'), magic: s7('W_Sword004.png'), rare: s7('W_Sword009.png'), sacred: s7('W_Gold_Sword.png') },
  weapon_sword_15:  { normal: s7('W_Sword005.png'), magic: s7('W_Sword007.png'), rare: s7('W_Sword012.png'), sacred: s7('W_Gold_Sword.png') },
  weapon_sword_30:  { normal: s7('W_Sword007.png'), magic: s7('W_Sword009.png'), rare: s7('W_Sword012.png'), sacred: s7('W_Gold_Sword.png') },
  weapon_axe_1:     { normal: s7('W_Axe001.png'),   magic: s7('W_Axe003.png'),   rare: s7('W_Axe007.png'),   sacred: s7('W_Gold_Axe.png') },
  weapon_axe_15:    { normal: s7('W_Axe005.png'),   magic: s7('W_Axe008.png'),   rare: s7('W_Axe011.png'),   sacred: s7('W_Gold_Axe.png') },
  weapon_axe_30:    { normal: s7('W_Axe008.png'),   magic: s7('W_Axe011.png'),   rare: s7('W_Axe011.png'),   sacred: s7('W_Gold_Axe.png') },
  weapon_bow_1:     { normal: s7('W_Bow01.png'),    magic: s7('W_Bow04.png'),    rare: s7('W_Bow08.png'),    sacred: s7('W_Gold_Bow.png') },
  weapon_bow_15:    { normal: s7('W_Bow06.png'),    magic: s7('W_Bow09.png'),    rare: s7('W_Bow13.png'),    sacred: s7('W_Gold_Bow.png') },
  weapon_bow_30:    { normal: s7('W_Bow09.png'),    magic: s7('W_Bow13.png'),    rare: s7('W_Bow13.png'),    sacred: s7('W_Gold_Bow.png') },
  weapon_staff_1:   { normal: s7('W_Staff01.png'),  magic: s7('W_Staff03.png'),  rare: s7('W_Staff05.png'),  sacred: s7('W_Staff07.png') },
  weapon_staff_15:  { normal: s7('W_Staff04.png'),  magic: s7('W_Staff05.png'),  rare: s7('W_Staff07.png'),  sacred: s7('W_Staff08.png') },
  weapon_staff_30:  { normal: s7('W_Staff05.png'),  magic: s7('W_Staff07.png'),  rare: s7('W_Staff08.png'),  sacred: s7('W_Staff08.png') },
  weapon_dagger_1:  { normal: s7('W_Dagger001.png'), magic: s7('W_Dagger002.png'), rare: s7('W_Dagger003.png'), sacred: s7('W_Dagger004.png') },
  weapon_dagger_15: { normal: s7('W_Dagger002.png'), magic: s7('W_Dagger003.png'), rare: s7('W_Dagger004.png'), sacred: s7('W_Dagger005.png') },
  weapon_dagger_30: { normal: s7('W_Dagger003.png'), magic: s7('W_Dagger004.png'), rare: s7('W_Dagger005.png'), sacred: s7('W_Dagger005.png') },
  weapon_wand_1:    { normal: s7('W_Staff01.png'),  magic: s7('W_Staff01.png'),  rare: s7('W_Staff03.png'),  sacred: s7('W_Staff05.png') },
  weapon_wand_15:   { normal: s7('W_Staff03.png'),  magic: s7('W_Staff03.png'),  rare: s7('W_Staff05.png'),  sacred: s7('W_Staff07.png') },
  weapon_wand_30:   { normal: s7('W_Staff05.png'),  magic: s7('W_Staff05.png'),  rare: s7('W_Staff07.png'),  sacred: s7('W_Staff08.png') },
  weapon_mace_1:    { normal: s7('W_Mace001.png'),  magic: s7('W_Mace003.png'),  rare: s7('W_Mace005.png'),  sacred: s7('W_Mace007.png') },
  weapon_mace_15:   { normal: s7('W_Mace003.png'),  magic: s7('W_Mace005.png'),  rare: s7('W_Mace007.png'),  sacred: s7('W_Mace007.png') },
  weapon_mace_30:   { normal: s7('W_Mace005.png'),  magic: s7('W_Mace007.png'),  rare: s7('W_Mace007.png'),  sacred: s7('W_Mace007.png') },

  // === Offhand/Shield ===
  offhand_ar_1:  { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },
  offhand_ar_15: { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },
  offhand_ar_30: { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },
  offhand_ev_1:  { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },
  offhand_ev_15: { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },
  offhand_ev_30: { normal: db32('shield_metal.png'), magic: db32('shield_metal.png'), rare: db32('shield_metal.png'), sacred: db32('shield_metal.png') },

  // === Amulets ===
  amulet_1:  { normal: s7('Ac_Necklace01.png'), magic: s7('Ac_Necklace03.png'), rare: s7('Ac_Necklace05.png'), sacred: s7('Ac_Necklace07.png') },
  amulet_15: { normal: s7('Ac_Necklace03.png'), magic: s7('Ac_Necklace05.png'), rare: s7('Ac_Necklace05.png'), sacred: s7('Ac_Necklace07.png') },
  amulet_30: { normal: s7('Ac_Necklace05.png'), magic: s7('Ac_Necklace05.png'), rare: s7('Ac_Necklace07.png'), sacred: s7('Ac_Necklace07.png') },

  // === Rings ===
  ring_1:  { normal: s7('Ac_Ring01.png'), magic: s7('Ac_Ring01.png'), rare: s7('Ac_Ring02.png'), sacred: s7('Ac_Ring02.png') },
  ring_15: { normal: s7('Ac_Ring01.png'), magic: s7('Ac_Ring02.png'), rare: s7('Ac_Ring02.png'), sacred: s7('Ac_Ring02.png') },
  ring_30: { normal: s7('Ac_Ring02.png'), magic: s7('Ac_Ring02.png'), rare: s7('Ac_Ring02.png'), sacred: s7('Ac_Ring02.png') },

  // === Capes ===
  cape_ar_1:  { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour02.png') },
  cape_ar_15: { normal: s7('A_Armour01.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },
  cape_ar_30: { normal: s7('A_Armour02.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
  cape_ev_1:  { normal: s7('A_Clothing01.png'), magic: s7('A_Clothing02.png'), rare: s7('A_Armour01.png'),  sacred: s7('A_Armour02.png') },
  cape_ev_15: { normal: s7('A_Clothing02.png'), magic: s7('A_Armour01.png'),   rare: s7('A_Armour02.png'),  sacred: s7('A_Armour03.png') },
  cape_ev_30: { normal: s7('A_Armour01.png'),   magic: s7('A_Armour02.png'),   rare: s7('A_Armour03.png'),  sacred: s7('A_Armour03.png') },
};

// Resolve icon path for a given baseId and rarity
export function getItemIconPath(baseId: string, rarity: ItemRarity): string | undefined {
  // 1. Custom folder icon (highest priority)
  if (loadedCustomIcons.has(baseId)) {
    return `${CUSTOM_ICONS_PATH}/${baseId}.png`;
  }

  // 2. Mapped icons (7soul/db32)
  const variants = ITEM_ICON_MAP[baseId] || REGISTRY_ICON_MAP[baseId];
  if (!variants) return undefined;

  switch (rarity) {
    case 'legendary':
    case 'set':
      return variants.sacred;
    case 'rare':
      return variants.rare;
    case 'magic':
      return variants.magic;
    default:
      return variants.normal;
  }
}

// Get all unique icon paths (for preloading)
export function getAllIconPaths(): string[] {
  const paths = new Set<string>();

  // All baseId variant icons (legacy + registry)
  for (const variants of Object.values(ITEM_ICON_MAP)) {
    paths.add(variants.normal);
    paths.add(variants.magic);
    paths.add(variants.rare);
    paths.add(variants.sacred);
  }
  for (const variants of Object.values(REGISTRY_ICON_MAP)) {
    paths.add(variants.normal);
    paths.add(variants.magic);
    paths.add(variants.rare);
    paths.add(variants.sacred);
  }

  // Slot fallback icons
  for (const path of Object.values(SLOT_FALLBACK_ICONS)) {
    paths.add(path);
  }

  // Misc icons
  for (const path of Object.values(MISC_ITEM_ICONS)) {
    paths.add(path);
  }

  return Array.from(paths);
}
